import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  useTheme,
  alpha,
  Container,
  Fade,
  LinearProgress,
  Divider,
  Button
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  AttachMoney as AttachMoneyIcon,
  Assessment as AssessmentIcon,
  AccessTime as AccessTimeIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { format, startOfWeek, endOfWeek, addDays, isSameDay, parseISO } from 'date-fns';
import { supabase } from '../../../supabaseClient';
import { usePermissions } from '../../../utils/permissions';
import { useNavigate } from 'react-router-dom';

const StatCard = ({ icon, title, value, subtitle, color, trend }) => {
  const theme = useTheme();
  
  return (
    <Card sx={{ 
      borderRadius: 3,
      border: `1px solid ${theme.palette.grey[400]}`,
      background: 'white',
      backdropFilter: 'blur(20px)',
      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
      position: 'relative',
      overflow: 'hidden',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
      },
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: alpha(color, 0.1),
            color: color
          }}>
            {icon}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 500, color: 'text.primary', mb: 0.5 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Typography variant="caption" sx={{ 
                display: 'block', 
                mt: 0.5,
                color: trend > 0 ? 'success.main' : trend < 0 ? 'error.main' : 'text.secondary'
              }}>
                {trend > 0 ? '+' : ''}{trend}% vs last week
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const Analytics = () => {
  const theme = useTheme();
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  
  const [currentDate] = useState(new Date());
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchEmployees(),
          fetchDepartments(),
          fetchTasks()
        ]);
      } catch (error) {
        console.error('Error fetching analytics data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (companyId) {
      fetchData();
    }
  }, [companyId]);

  const fetchEmployees = async () => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select(`
          *,
          department:departments(*)
        `)
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('first_name');

      if (error) throw error;
      setEmployees(data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    }
  };

  const fetchDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('*')
        .eq('company_id', companyId)
        .order('name');

      if (error) throw error;
      setDepartments(data || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchTasks = async () => {
    try {
      const weekStart = startOfWeek(currentDate);
      const weekEnd = endOfWeek(currentDate);

      const { data, error } = await supabase
        .from('employee_shifts')
        .select(`
          *,
          employee:employees(*),
          department:departments(*)
        `)
        .eq('company_id', companyId)
        .gte('shift_date', format(weekStart, 'yyyy-MM-dd'))
        .lte('shift_date', format(weekEnd, 'yyyy-MM-dd'))
        .order('shift_date')
        .order('start_time');

      if (error) throw error;
      setTasks(data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  // Calculate analytics data
  const calculateAnalytics = () => {
    const currentWeekStart = startOfWeek(currentDate);
    const currentWeekEnd = endOfWeek(currentDate);
    
    const currentWeekTasks = tasks.filter(task => 
      task.shift_date && 
      new Date(task.shift_date) >= currentWeekStart && 
      new Date(task.shift_date) <= currentWeekEnd
    );

    // Total tasks this week
    const totalTasks = currentWeekTasks.length;

    // Employees with tasks this week
    const employeesWithTasks = new Set(currentWeekTasks.map(task => task.employee_id)).size;

    // Calculate task distribution by department
    const departmentTaskCounts = {};
    departments.forEach(dept => {
      departmentTaskCounts[dept.id] = currentWeekTasks.filter(task => task.department_id === dept.id).length;
    });

    // Calculate daily task distribution
    const dailyTaskCounts = {};
    for (let i = 0; i < 7; i++) {
      const day = addDays(currentWeekStart, i);
      const dayKey = format(day, 'yyyy-MM-dd');
      dailyTaskCounts[dayKey] = currentWeekTasks.filter(task => task.shift_date === dayKey).length;
    }

    // Calculate task coverage (employees with documentation)
    const tasksWithDocumentation = currentWeekTasks.filter(task => task.role_documentation && task.role_documentation.trim()).length;
    const documentationCoverage = totalTasks > 0 ? (tasksWithDocumentation / totalTasks) * 100 : 0;

    return {
      totalTasks,
      employeesWithTasks,
      utilizationRate: (employeesWithTasks / Math.max(employees.length, 1)) * 100,
      departmentTaskCounts,
      dailyTaskCounts,
      tasksWithDocumentation,
      documentationCoverage
    };
  };

  const analytics = calculateAnalytics();

  // Permission check
  if (!hasPermission('schedule.view')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <AssessmentIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Task Analytics Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Task Analytics. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha('#673AB7', 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 40px ${alpha('#673AB7', 0.2)}`
          }
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha('#673AB7', 0.1),
                mb: 2
              }}>
                <AssessmentIcon sx={{ fontSize: 48, color: '#673AB7' }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{
              fontWeight: 500,
              color: '#673AB7',
              mb: 1,
              fontSize: '1.1rem'
            }}>
              Loading Task Analytics
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontWeight: 500,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 3
            }}>
              Analyzing task data and performance metrics...
            </Typography>
            <Box sx={{ 
              width: '60%', 
              mx: 'auto',
              '& .MuiLinearProgress-root': {
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.grey[300], 0.3),
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#673AB7',
                  borderRadius: 4
                }
              }
            }}>
              <LinearProgress />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Fade in timeout={800}>
      <Container
        maxWidth="xl"
        sx={{
          py: 3,
          pb: 8,
          px: { xs: 2, sm: 3 },
          width: '100%',
          maxWidth: '100vw !important'
        }}
      >
        {/* Page Header */}
        <Card sx={{
          borderRadius: 3,
          mt: 4,
          mb: 4,
          border: 'none',
          background: 'white',
          boxShadow: 'none',
          borderTop: '3px solid #673AB7',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: `0 4px 20px ${alpha('#673AB7', 0.15)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
              <Box sx={{
                p: 2,
                borderRadius: 3,
                background: alpha('#673AB7', 0.08),
              }}>
                <AssessmentIcon sx={{ fontSize: 32, color: '#673AB7' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" sx={{
                  fontWeight: 500,
                  color: '#673AB7', 
                  mb: 0.5,
                  fontSize: '2rem',
                  letterSpacing: '-0.5px'
                }}>
                  Task Analytics & Reports
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: 'text.secondary', 
                  fontWeight: 500,
                  fontSize: '1rem'
                }}>
                  Week of {format(startOfWeek(currentDate), 'MMM d')} - {format(endOfWeek(currentDate), 'MMM d, yyyy')}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Key Metrics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<ScheduleIcon />}
              title="Total Tasks"
              value={analytics.totalTasks}
              subtitle={`${analytics.totalTasks} tasks scheduled`}
              color={theme.palette.primary.main}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<GroupIcon />}
              title="Employee Utilization"
              value={`${analytics.employeesWithTasks}/${employees.length}`}
              subtitle={`${analytics.utilizationRate.toFixed(0)}% utilization rate`}
              color={theme.palette.success.main}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<AssignmentIcon />}
              title="Task Documentation"
              value={`${analytics.tasksWithDocumentation}/${analytics.totalTasks}`}
              subtitle={`${analytics.documentationCoverage.toFixed(0)}% documented`}
              color={theme.palette.info.main}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<TrendingUpIcon />}
              title="Avg Tasks Per Employee"
              value={analytics.employeesWithTasks > 0 ? (analytics.totalTasks / analytics.employeesWithTasks).toFixed(1) : '0'}
              subtitle={`${analytics.employeesWithTasks} active employees`}
              color={theme.palette.warning.main}
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* Department Task Breakdown */}
          <Grid item xs={12} md={6}>
            <Card sx={{ 
              borderRadius: 3,
              border: `1px solid ${theme.palette.grey[400]}`,
              background: 'white',
              backdropFilter: 'blur(20px)',
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon sx={{ fontSize: 20 }} />
                  Tasks by Department
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ mb: 3, p: 2, borderRadius: 2, backgroundColor: alpha(theme.palette.info.main, 0.05), border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'info.main', mb: 1 }}>
                      Total Company Tasks This Week: {analytics.totalTasks}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Percentages show each department's share of total company tasks
                    </Typography>
                  </Box>
                  
                  {departments.map((dept) => {
                    const deptTaskCount = analytics.departmentTaskCounts[dept.id] || 0;
                    const percentage = (deptTaskCount / Math.max(analytics.totalTasks, 1)) * 100;
                    const deptTasks = tasks.filter(s => s.department_id === dept.id);
                    const employeeCount = deptTasks.length > 0 ? new Set(deptTasks.map(s => s.employee_id)).size : 0;
                    
                    return (
                      <Box key={dept.id} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{dept.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {employeeCount} employees • {deptTaskCount} tasks
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {deptTaskCount} tasks
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {percentage.toFixed(1)}% of total
                            </Typography>
                          </Box>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={percentage} 
                          sx={{ 
                            height: 6, 
                            borderRadius: 3,
                            bgcolor: alpha(theme.palette.grey[300], 0.3)
                          }} 
                        />
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Daily Breakdown */}
          <Grid item xs={12} md={6}>
            <Card sx={{ 
              borderRadius: 3,
              border: `1px solid ${theme.palette.grey[400]}`,
              background: 'white',
              backdropFilter: 'blur(20px)',
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScheduleIcon sx={{ fontSize: 20 }} />
                  Daily Task Breakdown
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ mt: 2 }}>
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((dayName, index) => {
                    const weekStart = startOfWeek(currentDate);
                    const targetDay = addDays(weekStart, index);
                    const dayKey = format(targetDay, 'yyyy-MM-dd');
                    const dayTaskCount = analytics.dailyTaskCounts[dayKey] || 0;
                    
                    return (
                      <Box key={dayName} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{dayName}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {dayTaskCount} tasks
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Documentation Analysis */}
          <Grid item xs={12} md={6}>
            <Card sx={{ 
              borderRadius: 3,
              border: `1px solid ${theme.palette.grey[400]}`,
              background: 'white',
              backdropFilter: 'blur(20px)',
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssignmentIcon sx={{ fontSize: 20 }} />
                  Task Documentation
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2">Total Tasks:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {analytics.totalTasks}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2">Tasks with Documentation:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {analytics.tasksWithDocumentation}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2">Documentation Coverage:</Typography>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 500, 
                      color: analytics.documentationCoverage >= 80 ? 'success.main' : 
                             analytics.documentationCoverage >= 50 ? 'warning.main' : 'error.main'
                    }}>
                      {analytics.documentationCoverage.toFixed(1)}%
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>Employees with Tasks:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {analytics.employeesWithTasks}/{employees.length}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Average Tasks per Employee:</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {analytics.employeesWithTasks > 0 ? (analytics.totalTasks / analytics.employeesWithTasks).toFixed(1) : '0'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Task Organization Analysis */}
          <Grid item xs={12} md={6}>
            <Card sx={{ 
              borderRadius: 3,
              border: `1px solid ${theme.palette.grey[400]}`,
              background: 'white',
              backdropFilter: 'blur(20px)',
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon sx={{ fontSize: 20 }} />
                  Task Organization
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2">Total Employees:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {employees.length}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2">Employees with Tasks:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {analytics.employeesWithTasks}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2">Employee Utilization:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {analytics.utilizationRate.toFixed(0)}%
                    </Typography>
                  </Box>
                  
                  {analytics.documentationCoverage < 50 && analytics.totalTasks > 0 && (
                    <Box sx={{
                      p: 2,
                      borderRadius: 1,
                      backgroundColor: alpha(theme.palette.warning.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                      mt: 2
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'warning.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WarningIcon sx={{ fontSize: 16 }} />
                        Documentation Alert
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                        Only {analytics.documentationCoverage.toFixed(0)}% of tasks have role documentation
                      </Typography>
                    </Box>
                  )}
                  
                  {analytics.documentationCoverage >= 80 && analytics.totalTasks > 0 && (
                    <Box sx={{
                      p: 2,
                      borderRadius: 1,
                      backgroundColor: alpha(theme.palette.success.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                      mt: 2
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 16 }} />
                        Excellent Documentation
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                        {analytics.documentationCoverage.toFixed(0)}% of tasks have detailed documentation
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Fade>
  );
};

export default Analytics;